import { useEffect, useState, useRef } from "react";

const BOOT_LINES = [
  { text: "> Initializing portfolio system...", delay: 0 },
  { text: "> Loading modules [OK]", delay: 180 },
  { text: "> Compiling components [OK]", delay: 330 },
  { text: "> Mounting React tree [OK]", delay: 480 },
  { text: "> Rendering UI...", delay: 620 },
];

export default function PageLoader({ onDone }: { onDone: () => void }) {
  const [progress, setProgress] = useState(0);
  const [visibleLines, setVisibleLines] = useState<number[]>([]);
  const [fadeOut, setFadeOut] = useState(false);
  const [showName, setShowName] = useState(false);
  const [scanLine, setScanLine] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);

  // Animate progress
  useEffect(() => {
    const duration = 1300;
    const animate = (ts: number) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const p = Math.min((elapsed / duration) * 100, 100);
      setProgress(p);
      if (p < 100) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  // Boot lines appear sequentially
  useEffect(() => {
    BOOT_LINES.forEach((line, i) => {
      setTimeout(() => {
        setVisibleLines((prev) => [...prev, i]);
      }, line.delay + 200);
    });
    setTimeout(() => setShowName(true), 200);
  }, []);

  // Scan line animation
  useEffect(() => {
    const interval = setInterval(() => {
      setScanLine((v) => (v >= 100 ? 0 : v + 0.6));
    }, 16);
    return () => clearInterval(interval);
  }, []);

  // Trigger exit
  useEffect(() => {
    if (progress >= 100) {
      const t = setTimeout(() => {
        setFadeOut(true);
        setTimeout(onDone, 500);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [progress, onDone]);

  const clamped = Math.min(Math.round(progress), 100);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden transition-all duration-700 ${fadeOut ? "opacity-0 scale-105 pointer-events-none" : "opacity-100 scale-100"}`}
      style={{ background: "#080808" }}
    >
      {/* Grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* Scan line sweep */}
      <div
        className="absolute left-0 right-0 h-[2px] pointer-events-none"
        style={{
          top: `${scanLine}%`,
          background: "linear-gradient(90deg, transparent 0%, rgba(34,197,94,0.15) 40%, rgba(34,197,94,0.35) 50%, rgba(34,197,94,0.15) 60%, transparent 100%)",
          boxShadow: "0 0 12px rgba(34,197,94,0.2)",
        }}
      />

      {/* Corners decorations */}
      <div className="absolute top-6 left-6 w-8 h-8 pointer-events-none" style={{ borderLeft: "1px solid rgba(34,197,94,0.4)", borderTop: "1px solid rgba(34,197,94,0.4)" }} />
      <div className="absolute top-6 right-6 w-8 h-8 pointer-events-none" style={{ borderRight: "1px solid rgba(34,197,94,0.4)", borderTop: "1px solid rgba(34,197,94,0.4)" }} />
      <div className="absolute bottom-6 left-6 w-8 h-8 pointer-events-none" style={{ borderLeft: "1px solid rgba(34,197,94,0.4)", borderBottom: "1px solid rgba(34,197,94,0.4)" }} />
      <div className="absolute bottom-6 right-6 w-8 h-8 pointer-events-none" style={{ borderRight: "1px solid rgba(34,197,94,0.4)", borderBottom: "1px solid rgba(34,197,94,0.4)" }} />

      {/* Center glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[250px] rounded-full pointer-events-none" style={{ background: "radial-gradient(ellipse, rgba(34,197,94,0.06) 0%, transparent 70%)" }} />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-lg px-8 gap-10">

        {/* Big name reveal */}
        <div className="text-center overflow-hidden">
          <div
            className="font-mono tracking-[0.3em] uppercase transition-all duration-1000"
            style={{
              fontSize: "clamp(11px, 1.5vw, 13px)",
              color: "rgba(34,197,94,0.5)",
              letterSpacing: "0.4em",
              opacity: showName ? 1 : 0,
              transform: showName ? "translateY(0)" : "translateY(20px)",
            }}
          >
            HR · SAFIN
          </div>
          <div
            className="font-black uppercase leading-none mt-1 transition-all duration-700"
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(36px, 8vw, 72px)",
              color: "rgba(255,255,255,0.92)",
              letterSpacing: "-0.02em",
              opacity: showName ? 1 : 0,
              transform: showName ? "translateY(0)" : "translateY(30px)",
              transitionDelay: "100ms",
              textShadow: "0 0 60px rgba(34,197,94,0.15)",
            }}
          >
            SAFIN
          </div>
          <div
            className="font-mono uppercase mt-1 transition-all duration-700"
            style={{
              fontSize: "clamp(9px, 1.2vw, 11px)",
              color: "rgba(34,197,94,0.6)",
              letterSpacing: "0.5em",
              opacity: showName ? 1 : 0,
              transform: showName ? "translateY(0)" : "translateY(20px)",
              transitionDelay: "200ms",
            }}
          >
            Full Stack Developer
          </div>
        </div>

        {/* Terminal log */}
        <div
          className="w-full rounded-lg p-5 font-mono"
          style={{
            background: "rgba(0,0,0,0.4)",
            border: "1px solid rgba(34,197,94,0.12)",
          }}
        >
          <div className="flex items-center gap-2 mb-3 pb-2" style={{ borderBottom: "1px solid rgba(34,197,94,0.08)" }}>
            <div className="w-2 h-2 rounded-full bg-green-500/40" />
            <div className="w-2 h-2 rounded-full" style={{ background: "rgba(255,200,0,0.3)" }} />
            <div className="w-2 h-2 rounded-full" style={{ background: "rgba(255,80,80,0.3)" }} />
            <span className="text-[10px] text-gray-600 ml-1 tracking-widest">SYSTEM BOOT</span>
          </div>
          <div className="space-y-1.5 min-h-[90px]">
            {BOOT_LINES.map((line, i) => (
              <div
                key={i}
                className="text-[11px] leading-relaxed transition-all duration-300"
                style={{
                  color: visibleLines.includes(i)
                    ? i === BOOT_LINES.length - 1
                      ? "rgba(34,197,94,0.9)"
                      : "rgba(34,197,94,0.5)"
                    : "transparent",
                  transform: visibleLines.includes(i) ? "translateX(0)" : "translateX(-8px)",
                }}
              >
                {line.text}
                {i === BOOT_LINES.length - 1 && visibleLines.includes(i) && (
                  <span
                    className="inline-block w-[6px] h-[12px] ml-1 align-middle"
                    style={{
                      background: "rgba(34,197,94,0.8)",
                      animation: "blink 0.8s step-end infinite",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full flex flex-col gap-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-mono text-gray-600 tracking-widest uppercase">Loading</span>
            <span className="text-[11px] font-mono font-bold" style={{ color: "rgba(34,197,94,0.7)" }}>
              {clamped}%
            </span>
          </div>
          <div className="w-full h-[3px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div
              className="h-full rounded-full transition-all duration-100 relative"
              style={{
                width: `${clamped}%`,
                background: "linear-gradient(90deg, rgba(34,197,94,0.5) 0%, rgba(34,197,94,1) 100%)",
                boxShadow: "0 0 8px rgba(34,197,94,0.5)",
              }}
            />
          </div>
          {/* Tick marks */}
          <div className="flex items-center justify-between mt-0.5">
            {[0, 25, 50, 75, 100].map((tick) => (
              <span
                key={tick}
                className="text-[9px] font-mono transition-colors duration-300"
                style={{ color: clamped >= tick ? "rgba(34,197,94,0.5)" : "rgba(255,255,255,0.1)" }}
              >
                {tick}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
